import React, { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Reorder, useDragControls } from "framer-motion";
import { GripVertical, Plus, Pencil, Trash2, Eye, EyeOff, ExternalLink, Loader2 } from "lucide-react";
import { adminGetBrands, adminCreateBrand, adminUpdateBrand, adminDeleteBrand, adminReorderBrands, mediaUrl } from "@/lib/api";
import { AdminShell } from "@/components/admin/AdminShell";
import { MediaPickerDialog } from "@/components/admin/MediaPickerDialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";

const EMPTY = { name: "", description: "", industry: "", website: "", status: "Active", comingSoon: false, featured: false, isVisible: true, logoMediaId: null };
const inputStyle = { background: "var(--surface)", borderColor: "var(--card-border)", color: "var(--text)" };

function BrandRow({ brand, onEdit, onDelete, onToggleVisible }) {
  const controls = useDragControls();
  const logo = mediaUrl(brand.logoMediaId || brand.logo);
  return (
    <Reorder.Item value={brand} dragListener={false} dragControls={controls}
      data-testid="admin-brands-reorder-item"
      className="flex items-center gap-3 rounded-[12px] border px-3 py-3"
      style={{ background: "var(--surface)", borderColor: "var(--card-border)" }}>
      <button onPointerDown={(e) => controls.start(e)} data-testid="admin-brands-reorder-handle"
        className="cursor-grab touch-none px-1 active:cursor-grabbing" aria-label="Drag to reorder" style={{ color: "var(--muted)" }}>
        <GripVertical size={18} />
      </button>
      <div className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-md border" style={{ borderColor: "var(--card-border)", background: "var(--bg-elev-1)" }}>
        {logo ? <img src={logo} alt="" className="h-full w-full object-contain p-1" /> : <span style={{ color: "var(--accent)" }}>{brand.name?.charAt(0)}</span>}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <p className="truncate text-sm font-medium" style={{ color: "var(--text)" }}>{brand.name}</p>
          {brand.comingSoon && <span className="rounded-full border border-dashed px-2 py-0.5 text-[10px]" style={{ borderColor: "var(--card-border)", color: "var(--muted)" }}>Coming Soon</span>}
          {brand.featured && <span className="rounded-full px-2 py-0.5 text-[10px]" style={{ background: "rgba(140,106,61,0.14)", color: "var(--accent-fg)" }}>Featured</span>}
          {!brand.isVisible && <span className="rounded-full px-2 py-0.5 text-[10px]" style={{ background: "rgba(255,255,255,0.05)", color: "var(--muted)" }}>Hidden</span>}
        </div>
        <p className="truncate text-xs" style={{ color: "var(--muted)" }}>{brand.industry}</p>
      </div>
      <div className="flex shrink-0 items-center gap-1">
        <button onClick={() => onToggleVisible(brand)} data-testid="admin-brand-toggle-visible" aria-label="Toggle visibility"
          className="inline-flex h-8 w-8 items-center justify-center rounded-md border" style={{ borderColor: "var(--card-border)", color: "var(--muted)" }}>
          {brand.isVisible ? <Eye size={15} /> : <EyeOff size={15} />}
        </button>
        <button onClick={() => onEdit(brand)} data-testid="admin-brand-edit-button" aria-label="Edit"
          className="inline-flex h-8 w-8 items-center justify-center rounded-md border" style={{ borderColor: "var(--card-border)", color: "var(--text)" }}><Pencil size={14} /></button>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <button data-testid="admin-brand-delete-button" aria-label="Delete" className="inline-flex h-8 w-8 items-center justify-center rounded-md border" style={{ borderColor: "var(--card-border)", color: "var(--danger)" }}><Trash2 size={14} /></button>
          </AlertDialogTrigger>
          <AlertDialogContent style={{ background: "var(--surface)", borderColor: "var(--card-border)", color: "var(--text)" }}>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete {brand.name}?</AlertDialogTitle>
              <AlertDialogDescription style={{ color: "var(--muted)" }}>This removes the brand from the website. This action cannot be undone.</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel style={{ background: "transparent", borderColor: "var(--card-border)", color: "var(--text)" }}>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={() => onDelete(brand.id)} style={{ background: "var(--danger)", color: "#fff" }}>Delete</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Reorder.Item>
  );
}

export default function AdminBrands() {
  const qc = useQueryClient();
  const { data: serverBrands = [], isLoading } = useQuery({ queryKey: ["admin-brands"], queryFn: adminGetBrands });
  const [order, setOrder] = useState([]);
  const [editing, setEditing] = useState(null); // brand or EMPTY
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);

  useEffect(() => { setOrder(serverBrands); }, [serverBrands]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const openCreate = () => { setForm(EMPTY); setEditing({ __new: true }); };
  const openEdit = (b) => { setForm({ ...EMPTY, ...b }); setEditing(b); };

  const persistOrder = async (newOrder) => {
    setOrder(newOrder);
    try {
      await adminReorderBrands(newOrder.map((b) => b.id));
      qc.invalidateQueries({ queryKey: ["brands"] });
      toast.success("Order updated");
    } catch { toast.error("Failed to save order"); }
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Brand name is required"); return; }
    setSaving(true);
    try {
      const payload = {
        name: form.name, description: form.description, industry: form.industry, website: form.website,
        status: form.status, comingSoon: form.comingSoon, featured: form.featured, isVisible: form.isVisible,
        logoMediaId: form.logoMediaId ?? null, logo: form.logoMediaId ?? null,
      };
      if (editing?.__new) await adminCreateBrand(payload);
      else await adminUpdateBrand(editing.id, payload);
      await qc.invalidateQueries({ queryKey: ["admin-brands"] });
      qc.invalidateQueries({ queryKey: ["brands"] });
      toast.success(editing?.__new ? "Brand created" : "Brand updated");
      setEditing(null);
    } catch (err) { toast.error(err?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    try { await adminDeleteBrand(id); await qc.invalidateQueries({ queryKey: ["admin-brands"] }); qc.invalidateQueries({ queryKey: ["brands"] }); toast.success("Brand deleted"); }
    catch { toast.error("Delete failed"); }
  };

  const toggleVisible = async (b) => {
    try { await adminUpdateBrand(b.id, { isVisible: !b.isVisible }); await qc.invalidateQueries({ queryKey: ["admin-brands"] }); qc.invalidateQueries({ queryKey: ["brands"] }); }
    catch { toast.error("Update failed"); }
  };

  const pickedLogo = mediaUrl(form.logoMediaId);

  return (
    <AdminShell title="Brands" actions={
      <button onClick={openCreate} data-testid="admin-add-brand-button" className="inline-flex items-center gap-2 rounded-[10px] px-4 py-2 text-sm font-medium" style={{ background: "var(--accent)", color: "var(--accent-fg)" }}>
        <Plus size={15} /> Add Brand
      </button>
    }>
      <p className="mb-4 text-sm" style={{ color: "var(--muted)" }}>Drag to reorder. Changes to order, visibility, and details reflect on the website automatically.</p>
      {isLoading ? (
        <div className="space-y-2">{Array.from({ length: 4 }).map((_, i) => <div key={i} className="h-16 animate-pulse rounded-[12px] border" style={{ background: "var(--surface)", borderColor: "var(--card-border)" }} />)}</div>
      ) : (
        <Reorder.Group axis="y" values={order} onReorder={persistOrder} data-testid="admin-brands-reorder-list" className="space-y-2">
          {order.map((b) => (
            <BrandRow key={b.id} brand={b} onEdit={openEdit} onDelete={remove} onToggleVisible={toggleVisible} />
          ))}
        </Reorder.Group>
      )}

      {/* Editor dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto" style={{ background: "var(--surface)", borderColor: "var(--card-border)", color: "var(--text)" }}>
          <DialogHeader><DialogTitle>{editing?.__new ? "Add Brand" : "Edit Brand"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="mb-1.5 block text-xs" style={{ color: "var(--muted)" }}>Logo</label>
              <div className="flex items-center gap-3">
                <div className="flex h-16 w-16 items-center justify-center overflow-hidden rounded-md border" style={{ borderColor: "var(--card-border)", background: "var(--bg-elev-1)" }}>
                  {pickedLogo ? <img src={pickedLogo} alt="" className="h-full w-full object-contain p-1.5" /> : <span className="text-xs" style={{ color: "var(--muted)" }}>None</span>}
                </div>
                <button type="button" onClick={() => setPickerOpen(true)} data-testid="admin-brand-logo-picker" className="rounded-[10px] border px-3.5 py-2 text-sm" style={{ borderColor: "var(--card-border)", color: "var(--text)" }}>Choose Logo</button>
                {form.logoMediaId && <button type="button" onClick={() => set("logoMediaId", null)} className="text-xs" style={{ color: "var(--danger)" }}>Remove</button>}
              </div>
            </div>
            <div>
              <label className="mb-1.5 block text-xs" style={{ color: "var(--muted)" }}>Brand Name *</label>
              <input data-testid="admin-brand-name-input" value={form.name} onChange={(e) => set("name", e.target.value)} className="w-full rounded-[10px] border px-3.5 py-2.5 text-sm outline-none" style={inputStyle} />
            </div>
            <div>
              <label className="mb-1.5 block text-xs" style={{ color: "var(--muted)" }}>Short Description</label>
              <textarea data-testid="admin-brand-desc-input" rows={3} value={form.description} onChange={(e) => set("description", e.target.value)} className="w-full rounded-[10px] border px-3.5 py-2.5 text-sm outline-none" style={inputStyle} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="mb-1.5 block text-xs" style={{ color: "var(--muted)" }}>Industry</label>
                <input data-testid="admin-brand-industry-input" value={form.industry} onChange={(e) => set("industry", e.target.value)} className="w-full rounded-[10px] border px-3.5 py-2.5 text-sm outline-none" style={inputStyle} />
              </div>
              <div>
                <label className="mb-1.5 block text-xs" style={{ color: "var(--muted)" }}>Status</label>
                <input data-testid="admin-brand-status-input" value={form.status} onChange={(e) => set("status", e.target.value)} className="w-full rounded-[10px] border px-3.5 py-2.5 text-sm outline-none" style={inputStyle} />
              </div>
            </div>
            <div>
              <label className="mb-1.5 block text-xs" style={{ color: "var(--muted)" }}>Website URL</label>
              <input data-testid="admin-brand-website-input" value={form.website} onChange={(e) => set("website", e.target.value)} placeholder="https://" className="w-full rounded-[10px] border px-3.5 py-2.5 text-sm outline-none" style={inputStyle} />
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              {[["comingSoon", "Coming Soon"], ["featured", "Featured"], ["isVisible", "Visible"]].map(([k, label]) => (
                <label key={k} className="flex items-center justify-between rounded-[10px] border px-3 py-2.5" style={{ borderColor: "var(--card-border)" }}>
                  <span className="text-sm" style={{ color: "var(--text)" }}>{label}</span>
                  <Switch checked={!!form[k]} onCheckedChange={(v) => set(k, v)} data-testid={`admin-brand-${k}-switch`} />
                </label>
              ))}
            </div>
          </div>
          <DialogFooter>
            <button onClick={() => setEditing(null)} className="rounded-[10px] border px-4 py-2 text-sm" style={{ borderColor: "var(--card-border)", color: "var(--text)" }}>Cancel</button>
            <button onClick={save} disabled={saving} data-testid="admin-brand-save-button" className="inline-flex items-center gap-2 rounded-[10px] px-4 py-2 text-sm font-medium disabled:opacity-60" style={{ background: "var(--accent)", color: "var(--accent-fg)" }}>
              {saving && <Loader2 size={15} className="animate-spin" />} Save
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <MediaPickerDialog open={pickerOpen} onOpenChange={setPickerOpen} onSelect={(id) => set("logoMediaId", id)} />
    </AdminShell>
  );
}
